import type { InventoryRowEdit } from '..';
import type {
    ChangeLogActionFilter,
    ChangeLogTimeRange,
    CreatePoolDraft,
    InventoryRowsPerPage,
    PoolDetailTab,
    PoolStatusFilter,
    UploadCategory,
    UploadFileInfo,
    UploadMode,
    UploadStreamError,
    UploadWizardStep,
} from './types';
import type { InventoryParseResult } from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';

export type OpenPoolListAction = { type: 'OPEN_POOL_LIST' };
export const openPoolList = (): OpenPoolListAction => ({ type: 'OPEN_POOL_LIST' });

export type OpenPoolDetailAction = { type: 'OPEN_POOL_DETAIL'; poolId: string };
export const openPoolDetail = (poolId: string): OpenPoolDetailAction => ({
    type: 'OPEN_POOL_DETAIL',
    poolId,
});

export type SetPoolSearchQueryAction = { type: 'SET_POOL_SEARCH_QUERY'; query: string };
export const setPoolSearchQuery = (query: string): SetPoolSearchQueryAction => ({
    type: 'SET_POOL_SEARCH_QUERY',
    query,
});

export type SetPoolStatusFilterAction = { type: 'SET_POOL_STATUS_FILTER'; filter: PoolStatusFilter };
export const setPoolStatusFilter = (filter: PoolStatusFilter): SetPoolStatusFilterAction => ({
    type: 'SET_POOL_STATUS_FILTER',
    filter,
});

export type SetSymbolSearchQueryAction = { type: 'SET_SYMBOL_SEARCH_QUERY'; query: string };
export const setSymbolSearchQuery = (query: string): SetSymbolSearchQueryAction => ({
    type: 'SET_SYMBOL_SEARCH_QUERY',
    query,
});

export type SetAppliedSymbolSearchAction = { type: 'SET_APPLIED_SYMBOL_SEARCH'; query: string };
export const setAppliedSymbolSearch = (query: string): SetAppliedSymbolSearchAction => ({
    type: 'SET_APPLIED_SYMBOL_SEARCH',
    query,
});

export type SetAddSymbolDraftAction = { type: 'SET_ADD_SYMBOL_DRAFT'; value: string };
export const setAddSymbolDraft = (value: string): SetAddSymbolDraftAction => ({
    type: 'SET_ADD_SYMBOL_DRAFT',
    value,
});

export type StageRowEditAction = { type: 'STAGE_ROW_EDIT'; edit: InventoryRowEdit };
export const stageRowEdit = (edit: InventoryRowEdit): StageRowEditAction => ({
    type: 'STAGE_ROW_EDIT',
    edit,
});

export type ClearRowFieldEditAction = {
    type: 'CLEAR_ROW_FIELD_EDIT';
    rowId: string;
    field: 'currentQuantity' | 'currentLabPrice';
};
export const clearRowFieldEdit = (
    rowId: string,
    field: 'currentQuantity' | 'currentLabPrice',
): ClearRowFieldEditAction => ({
    type: 'CLEAR_ROW_FIELD_EDIT',
    rowId,
    field,
});

export type StageRowRemovalAction = { type: 'STAGE_ROW_REMOVAL'; rowId: string };
export const stageRowRemoval = (rowId: string): StageRowRemovalAction => ({
    type: 'STAGE_ROW_REMOVAL',
    rowId,
});

export type UnstageRowRemovalAction = { type: 'UNSTAGE_ROW_REMOVAL'; rowId: string };
export const unstageRowRemoval = (rowId: string): UnstageRowRemovalAction => ({
    type: 'UNSTAGE_ROW_REMOVAL',
    rowId,
});

export type ResetEditsAction = { type: 'RESET_EDITS' };
export const resetEdits = (): ResetEditsAction => ({ type: 'RESET_EDITS' });

/**
 * Drop a specific subset of staged changes — the rows whose backend
 * operations succeeded during a partial-failure submit. Anything
 * outside the two lists stays staged so the user can retry just the
 * failed pieces.
 */
export type ClearStagedChangesAction = {
    type: 'CLEAR_STAGED_CHANGES';
    rowEditIds: ReadonlyArray<string>;
    removedRowIds: ReadonlyArray<string>;
};
export const clearStagedChanges = (
    rowEditIds: ReadonlyArray<string>,
    removedRowIds: ReadonlyArray<string>,
): ClearStagedChangesAction => ({
    type: 'CLEAR_STAGED_CHANGES',
    rowEditIds,
    removedRowIds,
});

export type OpenSubmitReviewAction = { type: 'OPEN_SUBMIT_REVIEW' };
export const openSubmitReview = (): OpenSubmitReviewAction => ({ type: 'OPEN_SUBMIT_REVIEW' });

export type CloseSubmitReviewAction = { type: 'CLOSE_SUBMIT_REVIEW' };
export const closeSubmitReview = (): CloseSubmitReviewAction => ({ type: 'CLOSE_SUBMIT_REVIEW' });

/** Begin an async submit: clears any prior error and flags in-flight. */
export type StartSubmitAction = { type: 'START_SUBMIT' };
export const startSubmit = (): StartSubmitAction => ({ type: 'START_SUBMIT' });

/** End an async submit successfully — caller should also dispatch RESET_EDITS. */
export type SucceedSubmitAction = { type: 'SUCCEED_SUBMIT' };
export const succeedSubmit = (): SucceedSubmitAction => ({ type: 'SUCCEED_SUBMIT' });

/** End an async submit with an error message to surface to the user. */
export type FailSubmitAction = { type: 'FAIL_SUBMIT'; error: string };
export const failSubmit = (error: string): FailSubmitAction => ({ type: 'FAIL_SUBMIT', error });

/** Dismiss the lingering submit error (e.g. from a "Dismiss" button). */
export type ClearSubmitErrorAction = { type: 'CLEAR_SUBMIT_ERROR' };
export const clearSubmitError = (): ClearSubmitErrorAction => ({ type: 'CLEAR_SUBMIT_ERROR' });

/** Flag the CSV export as in flight so the download button shows a spinner. */
export type StartCsvDownloadAction = { type: 'START_CSV_DOWNLOAD' };
export const startCsvDownload = (): StartCsvDownloadAction => ({ type: 'START_CSV_DOWNLOAD' });

/** Clear the CSV-export in-flight flag (on success or failure). */
export type EndCsvDownloadAction = { type: 'END_CSV_DOWNLOAD' };
export const endCsvDownload = (): EndCsvDownloadAction => ({ type: 'END_CSV_DOWNLOAD' });

export type SetActivePoolDetailTabAction = { type: 'SET_ACTIVE_POOL_DETAIL_TAB'; tab: PoolDetailTab };
export const setActivePoolDetailTab = (tab: PoolDetailTab): SetActivePoolDetailTabAction => ({
    type: 'SET_ACTIVE_POOL_DETAIL_TAB',
    tab,
});

export type SetInventoryPageAction = { type: 'SET_INVENTORY_PAGE'; page: number };
export const setInventoryPage = (page: number): SetInventoryPageAction => ({ type: 'SET_INVENTORY_PAGE', page });

export type SetInventoryRowsPerPageAction = {
    type: 'SET_INVENTORY_ROWS_PER_PAGE';
    rowsPerPage: InventoryRowsPerPage;
};
export const setInventoryRowsPerPage = (rowsPerPage: InventoryRowsPerPage): SetInventoryRowsPerPageAction => ({
    type: 'SET_INVENTORY_ROWS_PER_PAGE',
    rowsPerPage,
});

export type SetChangeLogSymbolFilterAction = { type: 'SET_CHANGE_LOG_SYMBOL_FILTER'; query: string };
export const setChangeLogSymbolFilter = (query: string): SetChangeLogSymbolFilterAction => ({
    type: 'SET_CHANGE_LOG_SYMBOL_FILTER',
    query,
});

export type SetChangeLogActionFilterAction = { type: 'SET_CHANGE_LOG_ACTION_FILTER'; filter: ChangeLogActionFilter };
export const setChangeLogActionFilter = (filter: ChangeLogActionFilter): SetChangeLogActionFilterAction => ({
    type: 'SET_CHANGE_LOG_ACTION_FILTER',
    filter,
});

export type SetChangeLogTimeRangeAction = { type: 'SET_CHANGE_LOG_TIME_RANGE'; range: ChangeLogTimeRange };
export const setChangeLogTimeRange = (range: ChangeLogTimeRange): SetChangeLogTimeRangeAction => ({
    type: 'SET_CHANGE_LOG_TIME_RANGE',
    range,
});

export type SetChangeLogPageAction = { type: 'SET_CHANGE_LOG_PAGE'; page: number };
export const setChangeLogPage = (page: number): SetChangeLogPageAction => ({ type: 'SET_CHANGE_LOG_PAGE', page });

export type SetChangeLogRowsPerPageAction = {
    type: 'SET_CHANGE_LOG_ROWS_PER_PAGE';
    rowsPerPage: InventoryRowsPerPage;
};
export const setChangeLogRowsPerPage = (rowsPerPage: InventoryRowsPerPage): SetChangeLogRowsPerPageAction => ({
    type: 'SET_CHANGE_LOG_ROWS_PER_PAGE',
    rowsPerPage,
});

/** Open the Create Pool dialog with a clean slate (no error, not submitting). */
export type OpenCreatePoolAction = { type: 'OPEN_CREATE_POOL' };
export const openCreatePool = (): OpenCreatePoolAction => ({ type: 'OPEN_CREATE_POOL' });

/** Close the Create Pool dialog and drop its submit lifecycle in one shot. */
export type CloseCreatePoolAction = { type: 'CLOSE_CREATE_POOL' };
export const closeCreatePool = (): CloseCreatePoolAction => ({ type: 'CLOSE_CREATE_POOL' });

/** Merge form-field edits into the Create Pool draft. */
export type UpdateCreatePoolDraftAction = { type: 'UPDATE_CREATE_POOL_DRAFT'; patch: Partial<CreatePoolDraft> };
export const updateCreatePoolDraft = (patch: Partial<CreatePoolDraft>): UpdateCreatePoolDraftAction => ({
    type: 'UPDATE_CREATE_POOL_DRAFT',
    patch,
});

/** Begin the create-pool POST: clears any prior error and flags in-flight. */
export type StartCreatePoolAction = { type: 'START_CREATE_POOL' };
export const startCreatePool = (): StartCreatePoolAction => ({ type: 'START_CREATE_POOL' });

/** End the create-pool POST successfully; also closes the dialog. */
export type SucceedCreatePoolAction = { type: 'SUCCEED_CREATE_POOL' };
export const succeedCreatePool = (): SucceedCreatePoolAction => ({ type: 'SUCCEED_CREATE_POOL' });

/** End the create-pool POST with an error message rendered in the dialog. */
export type FailCreatePoolAction = { type: 'FAIL_CREATE_POOL'; error: string };
export const failCreatePool = (error: string): FailCreatePoolAction => ({ type: 'FAIL_CREATE_POOL', error });

export type OpenUploadWizardAction = { type: 'OPEN_UPLOAD_WIZARD' };
export const openUploadWizard = (): OpenUploadWizardAction => ({ type: 'OPEN_UPLOAD_WIZARD' });

export type CloseUploadWizardAction = { type: 'CLOSE_UPLOAD_WIZARD' };
export const closeUploadWizard = (): CloseUploadWizardAction => ({ type: 'CLOSE_UPLOAD_WIZARD' });

export type SetUploadStepAction = { type: 'SET_UPLOAD_STEP'; step: UploadWizardStep };
export const setUploadStep = (step: UploadWizardStep): SetUploadStepAction => ({ type: 'SET_UPLOAD_STEP', step });

export type SetUploadFileAction = {
    type: 'SET_UPLOAD_FILE';
    file: UploadFileInfo | null;
    parseResult: InventoryParseResult | null;
};
export const setUploadFile = (
    file: UploadFileInfo | null,
    parseResult: InventoryParseResult | null,
): SetUploadFileAction => ({ type: 'SET_UPLOAD_FILE', file, parseResult });

export type SetUploadCategoryFilterAction = { type: 'SET_UPLOAD_CATEGORY_FILTER'; category: UploadCategory };
export const setUploadCategoryFilter = (category: UploadCategory): SetUploadCategoryFilterAction => ({
    type: 'SET_UPLOAD_CATEGORY_FILTER',
    category,
});

export type SetUploadSymbolFilterAction = { type: 'SET_UPLOAD_SYMBOL_FILTER'; query: string };
export const setUploadSymbolFilter = (query: string): SetUploadSymbolFilterAction => ({
    type: 'SET_UPLOAD_SYMBOL_FILTER',
    query,
});

export type SetUploadModeAction = { type: 'SET_UPLOAD_MODE'; mode: UploadMode };
export const setUploadMode = (mode: UploadMode): SetUploadModeAction => ({ type: 'SET_UPLOAD_MODE', mode });

/**
 * Begin an async upload-apply: clears any prior error, flags in-flight,
 * and seeds progress at `0 / total` rows.
 */
export type StartUploadApplyAction = { type: 'START_UPLOAD_APPLY'; total: number };
export const startUploadApply = (total: number): StartUploadApplyAction => ({ type: 'START_UPLOAD_APPLY', total });

/** Advance the in-flight apply's progress to `completed` rows. */
export type AdvanceUploadProgressAction = { type: 'ADVANCE_UPLOAD_PROGRESS'; completed: number };
export const advanceUploadProgress = (completed: number): AdvanceUploadProgressAction => ({
    type: 'ADVANCE_UPLOAD_PROGRESS',
    completed,
});

/** End an async upload-apply successfully and surface the summary. */
export type SucceedUploadApplyAction = {
    type: 'SUCCEED_UPLOAD_APPLY';
    summary: { added: number; updated: number; removed: number; rejected: number };
};
export const succeedUploadApply = (summary: {
    added: number;
    updated: number;
    removed: number;
    rejected: number;
}): SucceedUploadApplyAction => ({ type: 'SUCCEED_UPLOAD_APPLY', summary });

/** End an async upload-apply with an error message. */
export type FailUploadApplyAction = { type: 'FAIL_UPLOAD_APPLY'; error: string };
export const failUploadApply = (error: string): FailUploadApplyAction => ({ type: 'FAIL_UPLOAD_APPLY', error });

export interface PushUploadStreamErrorAction {
    type: 'PUSH_UPLOAD_STREAM_ERROR';
    error: UploadStreamError;
}

/** Record a backend-streamed upload failure for the wizard's messages panel. */
export const pushUploadStreamError = (error: UploadStreamError): PushUploadStreamErrorAction => ({
    type: 'PUSH_UPLOAD_STREAM_ERROR',
    error,
});

/** Replace `uploadError` with the given value (or null to clear). */
export type SetUploadErrorAction = { type: 'SET_UPLOAD_ERROR'; error: string | null };
export const setUploadError = (error: string | null): SetUploadErrorAction => ({ type: 'SET_UPLOAD_ERROR', error });

export type PoolManagementAction =
    | OpenPoolListAction
    | OpenPoolDetailAction
    | SetPoolSearchQueryAction
    | SetPoolStatusFilterAction
    | SetSymbolSearchQueryAction
    | SetAppliedSymbolSearchAction
    | SetAddSymbolDraftAction
    | StageRowEditAction
    | ClearRowFieldEditAction
    | StageRowRemovalAction
    | UnstageRowRemovalAction
    | ResetEditsAction
    | ClearStagedChangesAction
    | OpenSubmitReviewAction
    | CloseSubmitReviewAction
    | StartSubmitAction
    | SucceedSubmitAction
    | FailSubmitAction
    | ClearSubmitErrorAction
    | StartCsvDownloadAction
    | EndCsvDownloadAction
    | SetActivePoolDetailTabAction
    | SetInventoryPageAction
    | SetInventoryRowsPerPageAction
    | SetChangeLogSymbolFilterAction
    | SetChangeLogActionFilterAction
    | SetChangeLogTimeRangeAction
    | SetChangeLogPageAction
    | SetChangeLogRowsPerPageAction
    | OpenCreatePoolAction
    | CloseCreatePoolAction
    | UpdateCreatePoolDraftAction
    | StartCreatePoolAction
    | SucceedCreatePoolAction
    | FailCreatePoolAction
    | OpenUploadWizardAction
    | CloseUploadWizardAction
    | SetUploadStepAction
    | SetUploadFileAction
    | SetUploadCategoryFilterAction
    | SetUploadSymbolFilterAction
    | SetUploadModeAction
    | StartUploadApplyAction
    | AdvanceUploadProgressAction
    | SucceedUploadApplyAction
    | FailUploadApplyAction
    | PushUploadStreamErrorAction
    | SetUploadErrorAction;
